import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslations } from "use-intl";

import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@lawline/ui/components/select";
import { Skeleton } from "@lawline/ui/components/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@lawline/ui/components/tabs";

import { TableSearch } from "@platform/components/ui/table-search";
import { CommunicationsTable } from "./communications-table";
import { communicationsQueryOptions } from "./communications.queries";
import type { CommunicationType } from "./communications.types";

// Kinds shown in the admin switcher, in tab order. Mirror apps/api
// communication.constants.ts.
const COMMUNICATION_TYPES: readonly CommunicationType[] = ["feedback", "upgrade_request"];

const DEFAULT_COMMUNICATION_TYPE: CommunicationType = "feedback";

// "all" = no status filter; otherwise a specific lifecycle state.
const STATUS_FILTERS = ["all", "open", "closed"] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

// Admin viewer for every message a user sends to staff. Tabs switch the message
// kind, one query per kind. The search box filters the loaded rows in the client.
export function CommunicationsPage() {
	const t = useTranslations("admin.communications");
	const [type, setType] = useState<CommunicationType>(DEFAULT_COMMUNICATION_TYPE);
	const [status, setStatus] = useState<StatusFilter>("open");
	const [query, setQuery] = useState("");
	const { data, isPending, error } = useQuery(communicationsQueryOptions(type));

	const q = query.trim().toLowerCase();
	const visible = (data ?? [])
		.filter((c) => status === "all" || c.status === status)
		.filter(
			(c) =>
				!q ||
				[c.userName, c.userEmail, c.tenantName, c.body].some((f) => f?.toLowerCase().includes(q)),
		);

	return (
		<section className="mx-auto mt-2 flex w-full max-w-3xl flex-col gap-6">
			<h1 className="text-2xl font-semibold">{t("title")}</h1>

			<Tabs value={type} onValueChange={(v) => setType(v as CommunicationType)}>
				<TabsList>
					{COMMUNICATION_TYPES.map((ct) => (
						<TabsTrigger key={ct} value={ct}>
							{t(`types.${ct}`)}
						</TabsTrigger>
					))}
				</TabsList>
			</Tabs>

			{isPending ? (
				<Skeleton className="h-24 w-full" />
			) : error ? (
				<p className="text-destructive text-sm">{t("error")}</p>
			) : (
				<div className="flex flex-col gap-3">
					<div className="flex gap-3">
						<div className="flex-1">
							<TableSearch value={query} onChange={setQuery} placeholder={t("searchPlaceholder")} />
						</div>
						<Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
							<SelectTrigger className="w-40">
								<SelectValue />
							</SelectTrigger>
							<SelectContent position="popper">
								{STATUS_FILTERS.map((s) => (
									<SelectItem key={s} value={s}>
										{t(`statusFilter.${s}`)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<CommunicationsTable rows={visible} />
				</div>
			)}
		</section>
	);
}
